import { Button, ColumnLayout, Form, Header, List, Modal, Multiselect, SpaceBetween } from '@cloudscape-design/components';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import React, { useEffect, useState } from 'react';
import { fetchWithWatch } from '../../../../../hooks/fetchWithWatch';
import { addNotification, buildBreadcrumbItems, getCookie, getUrlVersion, setData, setLoading, setMenu } from '../../../../commons/functions';
import { useGlobalState } from '../../../../context/GlobalStateContext';
import { CustomAppLayout } from '../../../layout';
import { useRouter } from '../../../router';
const _preserve = React.version;

// --- INTERFACE DEFINITIONS ---
interface ItemRouting {
  idRouting: number;
  routing: string;
  label: string;
  value: string;
}

interface MenuItem {
  idMenu: number;
  text: string;
  items?: MenuItem[];
  type?: string;
  href?: string;
  section?: string;
  jsonRouting: number[];
}

interface FlatItem {
  id: string;
  content: React.ReactNode;
}

// --- UTILITY FUNCTIONS ---

/**
 * Recursively flattens the nested menu structure into a simple array for the List component.
 * It uses marginLeft to visually represent hierarchy, but loses the parent context.
 */
function flattenMenus(
  items: MenuItem[],
  level: number = 0,
  result: FlatItem[] = []
): FlatItem[] {
  items.forEach(item => {
    result.push({
      id: String(item.idMenu),
      content: (<div style={{ marginLeft: (20 * level) }}>{item.text}</div>)
    });

    if (item.items && item.items.length > 0) {
      flattenMenus(item.items, level + 1, result);
    }
  });
  return result;
}

/**
 * Reconstructs the nested MenuItem array based on the new order of the flat list.
 * NOTE: This basic version only correctly reorders top-level items. 
 * Correctly reordering nested items without explicit parent IDs in the flat list is highly complex.
 */
const reconstructMenus = (
  originalMenus: MenuItem[],
  newFlatItems: FlatItem[]
): MenuItem[] => {
  const itemMap = new Map<number, MenuItem>();

  // 1. Create a map of all items (and clones them) for mutable reordering
  const mapItems = (items: MenuItem[]) => {
    items.forEach(item => {
      // Clone the item and its items array for safe modification
      itemMap.set(item.idMenu, { ...item, items: item.items ? [...item.items] : undefined });
      if (item.items) {
        mapItems(item.items);
      }
    });
  };
  mapItems(originalMenus);

  // 2. Filter the top-level IDs in the new flat order
  const topLevelIdsInNewOrder = newFlatItems
    .map(item => parseInt(item.id))
    .filter(id => originalMenus.some(menu => menu.idMenu === id)); // Only include IDs that were originally top-level

  // 3. Reorder the top-level menus based on the new ID order
  const reorderedTopMenus: MenuItem[] = [];
  const processedIds = new Set<number>();

  for (const id of topLevelIdsInNewOrder) {
    if (!processedIds.has(id)) {
      const item = itemMap.get(id);
      if (item) {
        reorderedTopMenus.push(item);
        processedIds.add(id);
      }
    }
  }

  return reorderedTopMenus;
};


// --- REACT COMPONENT ---
export const SystemRoutingOrderPage = () => {
  const { menu } = useGlobalState();
  // --- STATE HOOKS ---



  const { navigate } = useRouter();

  const [oMenu, setOMenu] = useState<MenuItem>();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [routings, setRoutings] = useState<ItemRouting[]>([]);
  const [selectedRoutings, setSelectedRoutings] = useState<ItemRouting[]>([]);
  const [flatItems, setFlatItems] = useState<FlatItem[]>([]);

  // --- API HANDLERS ---

  const handleGetAll = async () => {
    try {
      setLoading(true);
      const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-routing': '/private/system/routing/all',
          'JWT': getCookie("JWT"),
        },
        body: JSON.stringify({})
      });
      if (!response.ok) { throw new Error(`HTTP error: ${response.status}`); }

      const data = await response.json();
      if (data && data.menus) {
        setMenus(data.menus);
        setRoutings(data.routings);
        if (data.menu) {
          setMenu(data.menu);
        }
      }
    } catch (error: any) {
      addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMenuRouting = async () => {
    // This function saves the routing changes for the single menu item open in the modal (oMenu).
    if (!oMenu) return;

    const menuToSave: MenuItem = {
      ...oMenu,
      jsonRouting: selectedRoutings.map(r => r.idRouting),
    };

    try {
      setLoading(true);
      const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-routing': '/private/system/routing/savemenu',
          'JWT': getCookie("JWT"),
        },
        body: JSON.stringify(menuToSave)
      });
      if (!response.ok) { throw new Error(`HTTP error: ${response.status}`); }

      // Trigger a full data refresh to update the menu structure
      await handleGetAll();

      setOMenu(undefined); // Close the modal

    } catch (error: any) {
      addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMenuOrder = async (orderedMenuIds: number[]) => {
    try {
      setLoading(true);
      const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Assuming this endpoint handles the flat list of IDs
          'x-routing': '/private/system/routing/savemenuorder',
          'JWT': getCookie("JWT"),
        },
        // Send the flat array of ordered IDs
        body: JSON.stringify({ jsonRouting: orderedMenuIds })
      });
      if (!response.ok) { throw new Error(`HTTP error: ${response.status}`); }

      // A full data refresh is still the safest way to sync the UI
      await handleGetAll();
    } catch (error: any) {
      addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
    } finally {
      setLoading(false);
    }
  };


  // --- LOGIC HANDLERS ---

  const handleMenuItemSelect = (menuId: number) => {
    // Find the full MenuItem object recursively
    const findMenuItem = (items: MenuItem[]): MenuItem | undefined => {
      for (const item of items) {
        if (item.idMenu === menuId) return item;
        if (item.items) {
          const foundChild = findMenuItem(item.items);
          if (foundChild) return foundChild;
        }
      }
      return undefined;
    };

    const selectedItem = findMenuItem(menus);

    if (selectedItem) {
      setOMenu(selectedItem);

      // Set the selected routings for the Multiselect based on the selected item's data
      const currentRoutings = selectedItem.jsonRouting
        .map(id => routings.find(r => r.idRouting === id))
        .filter((r): r is ItemRouting => r !== undefined);

      setSelectedRoutings(currentRoutings);
    }
  };


  // --- EFFECTS ---

  // Initial data load
  useEffect(() => {
    handleGetAll();
  }, []);

  // Flatten menus whenever the menus state changes (triggered by handleGetAll)
  useEffect(() => {
    setFlatItems(flattenMenus(menus));
  }, [menus]);


  // --- RENDER ---

  return (
    <CustomAppLayout
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={buildBreadcrumbItems(menu)}
          expandAriaLabel="Show path"
          ariaLabel="Breadcrumbs"
        />
      }
      content={
        <SpaceBetween size="s">
          <Header variant="h1">Administración de Menú y Enrutamiento</Header>

          <ColumnLayout columns={2}>
            <div>
              <Header variant="h2">Orden del Menú</Header>
              <List
                ariaLabel="Menu list"
                items={flatItems}
                sortable
                onSortingChange={({ detail }) => {
                  // 1. Create a mutable copy of the new item order (the entire sorted list)
                  const newFlatItems: FlatItem[] = [...detail.items];

                  // 2. Update the flat state for immediate visual change
                  setFlatItems(newFlatItems);

                  // 🛑 FIX: Extract the ID of *every* item in the sorted flat list.
                  // This correctly captures all IDs (top-level and children) in the user-defined order.
                  const orderedMenuIds: number[] = newFlatItems.map(item => parseInt(item.id));

                  // 3. Trigger the save function with the flat ID array
                  handleSaveMenuOrder(orderedMenuIds);
                }}
                renderItem={item => item}
              />
            </div>
            {/* Right column content if needed */}
            <div>
              {/* You can show details about the selected menu item here */}
            </div>
          </ColumnLayout>

          {/* --- MODAL FOR ROUTING CONFIGURATION --- */}
          <Modal
            size="large"
            visible={oMenu !== undefined}
            onDismiss={() => setOMenu(undefined)}
            header={`Enrutamiento de ${oMenu?.text}`}
          >
            <Form header={
              <Header variant="h2">Enrutamiento de {oMenu?.text}</Header>
            }
              actions={
                <Button variant="primary" onClick={handleSaveMenuRouting}>
                  Guardar cambios
                </Button>
              }>

              <Multiselect
                selectedOptions={selectedRoutings}
                filteringType="auto"
                onChange={({ detail }) =>
                  setSelectedRoutings(detail.selectedOptions as ItemRouting[])
                }
                options={routings}
                placeholder="Seleccione uno o más enrutamientos"
              />
            </Form>
          </Modal>
        </SpaceBetween>
      }
    />
  );
};